/**
 * Admin Application List Page
 *
 * Shows all applications across the system for admin oversight.
 * Admins can view, filter, and manage applications from all users.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Search, Filter, Clock, CheckCircle2, AlertCircle, XCircle, Eye } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { formatDate } from '../../utils/formatting';

interface Application {
  id: string;
  applicationNumber: string;
  companyName: string;
  applicationType: 'HALAL' | 'TOYYIBAN';
  status: 'SUBMITTED' | 'UNDER_REVIEW' | 'AUDIT_SCHEDULED' | 'AUDIT_IN_PROGRESS' | 'APPROVED' | 'REJECTED' | 'MORE_INFO_REQUIRED';
  submittedDate: string;
  lastActivity?: string;
  assignedTo?: string;
  assignedRole?: 'consultant' | 'auditor';
  priority?: 'high' | 'medium' | 'low';
}

export const ApplicationListPage: React.FC = () => {
  const { t } = useTranslation(['common', 'admin']);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState<Application[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setApplications([
        {
          id: 'app-001',
          applicationNumber: 'APP-2025-001',
          companyName: 'ABC Food Manufacturing Sdn Bhd',
          applicationType: 'HALAL',
          status: 'APPROVED',
          submittedDate: '2025-12-10',
          lastActivity: '2025-12-25',
          assignedTo: 'Sarah Ahmad',
          assignedRole: 'auditor',
          priority: 'medium',
        },
        {
          id: 'app-002',
          applicationNumber: 'APP-2025-002',
          companyName: 'Premium Bakery Industries',
          applicationType: 'HALAL',
          status: 'AUDIT_IN_PROGRESS',
          submittedDate: '2025-12-15',
          lastActivity: '2025-12-28',
          assignedTo: 'Sarah Ahmad',
          assignedRole: 'auditor',
          priority: 'high',
        },
        {
          id: 'app-003',
          applicationNumber: 'APP-2025-003',
          companyName: 'Global Ingredients Supply Co.',
          applicationType: 'HALAL',
          status: 'AUDIT_SCHEDULED',
          submittedDate: '2025-12-18',
          lastActivity: '2026-01-02',
          assignedTo: 'Dr. Mohamed Halal Consultant',
          assignedRole: 'consultant',
          priority: 'medium',
        },
        {
          id: 'app-004',
          applicationNumber: 'APP-2025-004',
          companyName: 'Tasty Snacks Manufacturing',
          applicationType: 'HALAL',
          status: 'UNDER_REVIEW',
          submittedDate: '2025-12-22',
          lastActivity: '2025-12-27',
          assignedTo: 'Ali Consultant',
          assignedRole: 'consultant',
          priority: 'high',
        },
        {
          id: 'app-005',
          applicationNumber: 'APP-2025-005',
          companyName: 'Halal Catering Services',
          applicationType: 'HALAL',
          status: 'SUBMITTED',
          submittedDate: '2025-12-28',
          lastActivity: '2025-12-28',
          priority: 'low',
        },
        {
          id: 'app-006',
          applicationNumber: 'APP-2025-006',
          companyName: 'Natural Cosmetics Sdn Bhd',
          applicationType: 'TOYYIBAN',
          status: 'MORE_INFO_REQUIRED',
          submittedDate: '2025-12-20',
          lastActivity: '2025-12-26',
          assignedTo: 'Ali Consultant',
          assignedRole: 'consultant',
          priority: 'medium',
        },
      ]);
      setLoading(false);
    }, 500);
  }, []);

  const getStatusBadge = (status: Application['status']) => {
    switch (status) {
      case 'APPROVED':
        return <Badge variant="success" dot>Approved</Badge>;
      case 'SUBMITTED':
        return <Badge variant="info" dot>Submitted</Badge>;
      case 'UNDER_REVIEW':
        return <Badge variant="warning" dot>Under Review</Badge>;
      case 'AUDIT_SCHEDULED':
        return <Badge variant="primary" dot>Audit Scheduled</Badge>;
      case 'AUDIT_IN_PROGRESS':
        return <Badge variant="warning" dot>Audit In Progress</Badge>;
      case 'REJECTED':
        return <Badge variant="error" dot>Rejected</Badge>;
      case 'MORE_INFO_REQUIRED':
        return <Badge variant="warning" dot>More Info Required</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const getStatusIcon = (status: Application['status']) => {
    switch (status) {
      case 'APPROVED':
        return <CheckCircle2 className="w-5 h-5 text-success-600" />;
      case 'SUBMITTED':
        return <Clock className="w-5 h-5 text-info-600" />;
      case 'UNDER_REVIEW':
      case 'AUDIT_IN_PROGRESS':
        return <Clock className="w-5 h-5 text-warning-600" />;
      case 'AUDIT_SCHEDULED':
        return <CheckCircle2 className="w-5 h-5 text-primary-600" />;
      case 'REJECTED':
        return <XCircle className="w-5 h-5 text-error-600" />;
      case 'MORE_INFO_REQUIRED':
        return <AlertCircle className="w-5 h-5 text-warning-600" />;
    }
  };

  const getPriorityBadge = (priority?: string) => {
    if (!priority) return null;
    switch (priority) {
      case 'high':
        return <Badge variant="error">High Priority</Badge>;
      case 'medium':
        return <Badge variant="warning">Medium Priority</Badge>;
      case 'low':
        return <Badge variant="info">Low Priority</Badge>;
    }
  };

  const filteredApplications = applications.filter((app) => {
    const matchesSearch = app.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.applicationNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    const matchesRole = roleFilter === 'all' || app.assignedRole === roleFilter;
    return matchesSearch && matchesStatus && matchesRole;
  });

  if (loading) {
    return (
      <PageLayout
        pageTitle="Applications"
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: 'Applications' },
        ]}
      >
        <div className="animate-pulse space-y-4">
          <div className="h-20 bg-gray-200 rounded-lg"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle="Applications"
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: 'Applications' },
      ]}
    >
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Applications</p>
                <p className="text-2xl font-bold text-gray-900">{applications.length}</p>
              </div>
              <ClipboardList className="w-8 h-8 text-primary-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Review</p>
                <p className="text-2xl font-bold text-warning-600">
                  {applications.filter(a => a.status === 'SUBMITTED' || a.status === 'UNDER_REVIEW').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-warning-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-info-600">
                  {applications.filter(a => a.status === 'AUDIT_SCHEDULED' || a.status === 'AUDIT_IN_PROGRESS').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-info-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Approved</p>
                <p className="text-2xl font-bold text-success-600">
                  {applications.filter(a => a.status === 'APPROVED').length}
                </p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-success-600" />
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by company name or application number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Statuses</option>
                <option value="SUBMITTED">Submitted</option>
                <option value="UNDER_REVIEW">Under Review</option>
                <option value="AUDIT_SCHEDULED">Audit Scheduled</option>
                <option value="AUDIT_IN_PROGRESS">Audit In Progress</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
                <option value="MORE_INFO_REQUIRED">More Info Required</option>
              </select>
            </div>

            {/* Role Filter */}
            <div className="flex items-center gap-2">
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Assignees</option>
                <option value="consultant">Consultants</option>
                <option value="auditor">Auditors</option>
              </select>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Application List */}
      {filteredApplications.length === 0 ? (
        <EmptyState
          icon={<ClipboardList className="w-12 h-12" />}
          title="No Applications Found"
          description="No applications match your current filters."
        />
      ) : (
        <div className="space-y-4">
          {filteredApplications.map((app) => (
            <Card
              key={app.id}
              hover
              className="cursor-pointer transition-shadow"
              onClick={() => navigate(`/admin/applications/${app.id}`)}
            >
              <CardBody>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    {/* Status Icon */}
                    <div className="mt-1">
                      {getStatusIcon(app.status)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {app.applicationNumber}
                        </h3>
                        {getStatusBadge(app.status)}
                        {getPriorityBadge(app.priority)}
                        <Badge variant="primary">{app.applicationType}</Badge>
                      </div>

                      <p className="text-base text-gray-900 mb-3 font-medium">{app.companyName}</p>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Submitted:</span>{' '}
                          {formatDate(app.submittedDate)}
                        </div>
                        {app.lastActivity && (
                          <div>
                            <span className="font-medium">Last Activity:</span>{' '}
                            {formatDate(app.lastActivity)}
                          </div>
                        )}
                        {app.assignedTo && (
                          <div>
                            <span className="font-medium">Assigned To:</span>{' '}
                            {app.assignedTo} ({app.assignedRole})
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<Eye className="w-4 h-4" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/admin/applications/${app.id}`);
                    }}
                  >
                    View Details
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </PageLayout>
  );
};
